import { create } from 'zustand'
import {
  campaignProgress,
  isChapterUnlocked
} from '../progression/campaignProgress'
import {
  emptyResearchState,
  type ResearchBranchId,
  type ResearchState,
  type ResearchTowerId,
  type TowerResearchRanks
} from '../game/combat/research/types'
import { purchaseResearchRank, type PurchaseResearchResult } from '../game/combat/research/purchase'
import { researchStorage } from '../game/combat/research/storage'
import { isAdvancedRank, researchCostForNextRank } from '../game/combat/research/costs'
import { RESEARCH_MAX_RANK } from '../game/combat/research/types'

/**
 * Authoritative reactive meta economy (Ingots + Research).
 * Persistence: campaign progress.v3 (Ingots) + research.v1 (ranks).
 * GameSession never reads this store — research is snapshotted at session create.
 */
interface MetaStore {
  ingots: number
  research: ResearchState
  /** Advanced Research (ranks beyond base) — unlocked when Chapter 2 is reachable. */
  advancedResearchUnlocked: boolean
  /** Monotonic revision for UI invalidation / double-click guards. */
  revision: number

  hydrate: () => void
  /** Sync Ingots from campaign progress persistence (after victory / load). */
  syncIngots: () => void
  setIngots: (ingots: number) => void
  purchase: (towerId: ResearchTowerId, branchId: ResearchBranchId) => PurchaseResearchResult
  resetMeta: () => void
  getTowerRanks: (towerId: ResearchTowerId) => TowerResearchRanks
  nextCost: (towerId: ResearchTowerId, branchId: ResearchBranchId) => number | null
  canAfford: (towerId: ResearchTowerId, branchId: ResearchBranchId) => boolean
  /** True when the next rank on this branch is Advanced and still locked. */
  isAdvancedLocked: (towerId: ResearchTowerId, branchId: ResearchBranchId) => boolean
}

let purchaseLock = false

function readAdvancedUnlocked(): boolean {
  return isChapterUnlocked('chapter-2', campaignProgress.load())
}

export const useMetaStore = create<MetaStore>((set, get) => ({
  ingots: 0,
  research: emptyResearchState(),
  advancedResearchUnlocked: false,
  revision: 0,

  hydrate: () => {
    const research = researchStorage.load()
    const ingots = campaignProgress.getIngots()
    set({
      research,
      ingots,
      advancedResearchUnlocked: readAdvancedUnlocked(),
      revision: get().revision + 1
    })
  },

  syncIngots: () => {
    set({
      ingots: campaignProgress.getIngots(),
      advancedResearchUnlocked: readAdvancedUnlocked(),
      revision: get().revision + 1
    })
  },

  setIngots: (ingots: number) => {
    set({ ingots: Math.max(0, Math.floor(ingots)), revision: get().revision + 1 })
  },

  purchase: (towerId, branchId) => {
    if (purchaseLock) {
      return { ok: false, reason: 'INSUFFICIENT_INGOTS' }
    }
    purchaseLock = true
    try {
      const { research, ingots } = get()
      const progress = campaignProgress.load()
      const advancedUnlocked = isChapterUnlocked('chapter-2', progress)
      const result = purchaseResearchRank({
        state: research,
        ingots,
        towerId,
        branchId,
        advancedUnlocked
      })
      if (!result.ok) return result

      // Debit campaign progress Ingots atomically with research persist.
      if (progress.ingots < result.cost) {
        return { ok: false, reason: 'INSUFFICIENT_INGOTS' }
      }
      const spent = campaignProgress.spendIngots(result.cost)
      if (!spent.ok) {
        return { ok: false, reason: 'INSUFFICIENT_INGOTS' }
      }

      researchStorage.save(result.stateAfter)
      set({
        research: result.stateAfter,
        ingots: spent.ingotsAfter,
        advancedResearchUnlocked: advancedUnlocked,
        revision: get().revision + 1
      })
      return { ...result, ingotsAfter: spent.ingotsAfter }
    } finally {
      purchaseLock = false
    }
  },

  resetMeta: () => {
    campaignProgress.resetProgress()
    researchStorage.clear()
    set({
      ingots: 0,
      research: emptyResearchState(),
      advancedResearchUnlocked: false,
      revision: get().revision + 1
    })
  },

  getTowerRanks: (towerId) => get().research.towers[towerId],

  nextCost: (towerId, branchId) => {
    const rank = get().research.towers[towerId][branchId]
    return researchCostForNextRank(branchId, rank)
  },

  isAdvancedLocked: (towerId, branchId) => {
    const rank = get().research.towers[towerId][branchId]
    if (rank >= RESEARCH_MAX_RANK[branchId]) return false
    return isAdvancedRank(branchId, rank + 1) && !get().advancedResearchUnlocked
  },

  canAfford: (towerId, branchId) => {
    const rank = get().research.towers[towerId][branchId]
    if (rank >= RESEARCH_MAX_RANK[branchId]) return false
    if (get().isAdvancedLocked(towerId, branchId)) return false
    const cost = researchCostForNextRank(branchId, rank)
    if (cost == null) return false
    return get().ingots >= cost
  }
}))

/** Boot-time hydrate for browser. Safe to call multiple times. */
export function hydrateMetaStore(): void {
  useMetaStore.getState().hydrate()
}

/** Multi-tab refresh when another tab writes localStorage. */
export function bindMetaStorageListener(): () => void {
  if (typeof window === 'undefined') return () => undefined
  const onStorage = (event: StorageEvent) => {
    if (
      event.key === 'shapes-td.progress.v3' ||
      event.key === 'shapes-td.progress.v2' ||
      event.key === 'shapes-td.research.v1' ||
      event.key === null
    ) {
      useMetaStore.getState().hydrate()
    }
  }
  window.addEventListener('storage', onStorage)
  return () => window.removeEventListener('storage', onStorage)
}
